import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiHolder, apis, AiExpertRole, initialAiExpertsQuotaMap, initialAiExpertsPermissionMap, } from 'api';
const aiStaffApi = apiHolder.api.requireLogicalApi(apis.aiStaffApiImpl);
const initialState = {
    teamInfoLoading: false,
    teamInfo: null,
    marketingExperts: [],
    siteExperts: [],
    snsExperts: [],
    operationExperts: [],
    expertsQuotaMap: Object.assign({}, initialAiExpertsQuotaMap),
    expertsPermissionMap: Object.assign({}, initialAiExpertsPermissionMap),
    configInfo: {
        [AiExpertRole.marketing]: {},
        [AiExpertRole.site]: {},
        [AiExpertRole.operations]: {},
        [AiExpertRole.sns]: {},
    },
};
const getAiTeamIdFromServer = async (config) => {
    const { exists, team_id } = await aiStaffApi.isAiTeamExist(config);
    if (!exists)
        return Promise.reject();
    return team_id;
};
export const getAiTeamDetailAsync = createAsyncThunk('getAiTeamDetail', async (params) => {
    let { teamId, config } = params || {};
    if (!teamId) {
        teamId = await getAiTeamIdFromServer(config);
    }
    const teamInfo = await aiStaffApi.getAiTeamDeatil({ team_id: teamId }, config);
    return teamInfo;
});
export const getAiTeamExpertsQuotas = createAsyncThunk('getAiTeamExpertsQuotas', async (params) => {
    const { config } = params || {};
    return await aiStaffApi.getAiTeamExpertsQuotas(config);
});
export const getAiTeamExpertsPermissions = createAsyncThunk('getAiTeamExpertsPermissions', async (params) => {
    const { config } = params || {};
    return await aiStaffApi.getAiTeamExpertsPermissions(config);
});
export const initAiTeamState = createAsyncThunk('initAiTeamState', async (params, { dispatch }) => {
    const requests = params.requests || ['teamInfo', 'expertsQuotas', 'expertsPermissions'];
    const promises = [];
    if (requests.includes('teamInfo')) {
        promises.push(dispatch(getAiTeamDetailAsync(params)));
    }
    if (requests.includes('expertsQuotas')) {
        promises.push(dispatch(getAiTeamExpertsQuotas(params)));
    }
    if (requests.includes('expertsPermissions')) {
        promises.push(dispatch(getAiTeamExpertsPermissions(params)));
    }
    return await Promise.all(promises);
});
export const refreshAiTeamState = initAiTeamState;
const aiTeamSlice = createSlice({
    name: 'aiTeamReducer',
    initialState,
    reducers: {
        update(state, action) {
            const payload = typeof action.payload === 'function' ? action.payload(state) : action.payload;
            Object.entries(payload).forEach(([key, value]) => {
                state[key] = value;
            });
        },
        updateConfigInfo(state, action) {
            const { role, data } = action.payload;
            state.configInfo = Object.assign(Object.assign({}, state.configInfo), { [role]: Object.assign(Object.assign({}, state.configInfo[role]), data) });
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getAiTeamDetailAsync.fulfilled, (state, action) => {
            var _a, _b, _c, _d, _e;
            state.teamInfo = (_a = action.payload) !== null && _a !== void 0 ? _a : null;
            state.marketingExperts = (_b = action.payload.marketing_expert) !== null && _b !== void 0 ? _b : [];
            state.siteExperts = (_c = action.payload.website_building_expert) !== null && _c !== void 0 ? _c : [];
            state.snsExperts = (_d = action.payload.social_media_expert) !== null && _d !== void 0 ? _d : [];
            state.operationExperts = (_e = action.payload.operation_expert) !== null && _e !== void 0 ? _e : [];
        })
            .addCase(getAiTeamExpertsQuotas.fulfilled, (state, action) => {
            state.expertsQuotaMap = action.payload;
        })
            .addCase(getAiTeamExpertsPermissions.fulfilled, (state, action) => {
            state.expertsPermissionMap = action.payload;
        })
            .addCase(initAiTeamState.pending, (state, action) => {
            var _a;
            if ((_a = action.meta.arg) === null || _a === void 0 ? void 0 : _a.showLoading) {
                state.teamInfoLoading = true;
            }
        })
            .addCase(initAiTeamState.fulfilled, (state, action) => {
            var _a;
            if ((_a = action.meta.arg) === null || _a === void 0 ? void 0 : _a.showLoading) {
                state.teamInfoLoading = false;
            }
        })
            .addCase(initAiTeamState.rejected, (state, action) => {
            var _a;
            if ((_a = action.meta.arg) === null || _a === void 0 ? void 0 : _a.showLoading) {
                state.teamInfoLoading = false;
            }
        });
    },
});
export const getExpertByRole = (state, role) => {
    switch (role) {
        case AiExpertRole.marketing:
            return state.marketingExperts[0] || null;
        case AiExpertRole.site:
            return state.siteExperts[0] || null;
        case AiExpertRole.sns:
            return state.snsExperts[0] || null;
        case AiExpertRole.operations:
            return state.operationExperts[0] || null;
        default:
            return null;
    }
};
export const getExpertListByRole = (state, role) => {
    switch (role) {
        case AiExpertRole.marketing:
            return state.marketingExperts || null;
        case AiExpertRole.site:
            return state.siteExperts || null;
        case AiExpertRole.sns:
            return state.snsExperts || null;
        case AiExpertRole.operations:
            return state.operationExperts || null;
        default:
            return null;
    }
};
export const { actions } = aiTeamSlice;
export default aiTeamSlice.reducer;
